/**
 * File Download Utility
 * Helper functions for downloading and opening documents
 */

/**
 * Save an in-memory blob to the user's computer under `fileName`.
 *
 * The anchor is appended to the document before it is clicked: Firefox ignores a click on a
 * link that is not in the DOM. The object URL is revoked straight after — the browser has
 * already taken its own reference to the blob by then.
 */
export const downloadBlobFile = (blob: Blob, fileName: string): void => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};

/**
 * Hand a URL to the browser's own download manager.
 *
 * For a large file (a game video can be gigabytes) this beats fetching it into a blob: nothing
 * is held in page memory, and the browser's downloads UI shows progress and lets the coach
 * cancel. The URL must answer with `Content-Disposition: attachment` for the browser to save
 * it rather than navigate to it; the `download` attribute is ignored cross-origin, so it is
 * only a hint. Appended before the click for the same Firefox reason as above.
 */
export const startUrlDownload = (url: string, fileName: string): void => {
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
};

/**
 * Download a file from a URL and open it in a new tab
 * For browser-viewable files (PDF, images), they will render in the browser
 * For other files (Word, Excel), they will download
 */
export const downloadAndOpenDocument = async (
  gcsUrl: string,
  fileName: string,
  contentType: string
): Promise<void> => {
  try {
    // For PDFs and images, we can open directly in a new tab
    const viewableTypes = [
      'application/pdf',
      'image/png',
      'image/jpeg',
      'image/jpg',
    ];

    if (viewableTypes.includes(contentType)) {
      // Open in new tab - browser will render it
      window.open(gcsUrl, '_blank', 'noopener,noreferrer');
    } else {
      // For Word/Excel docs, fetch and trigger download
      const response = await fetch(gcsUrl);

      if (!response.ok) {
        throw new Error(`Failed to download file: ${response.status} ${response.statusText}`);
      }

      downloadBlobFile(await response.blob(), fileName);
    }
  } catch (error) {
    console.error('[FileDownload] Failed to open document', {
      error,
      fileName,
      contentType,
    });
    throw error;
  }
};
